import { CSSProperties, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import Section from './Section';
import { formatDollarAmount } from '../../utils/global/helper';
import useResumeSubscription from '../../hooks/subscriptions/useResumeSubscription';
import useSubscriptionPortal from '../../hooks/subscriptions/useSubscriptionPortal';
import useConfirm from '../../hooks/useConfirm';
import useGetSubscriptionFromStripe from '../../hooks/subscriptions/useGetSubscriptionFromStripe';
import useResubscribeSubscription from '../../hooks/subscriptions/useResubscribeSubscription';
import { ROUTES } from '../../constants/global';
import useGetSubscription from '../../hooks/subscriptions/useGetSubscription';
import useUser from '../../hooks/global/useUser';
import FreeSubscriptionModal from './FreeSubscriptionModal';

const sharedButtonStyle: SxProps<Theme> = {
  marginTop: 1.5,
  width: 'fit-content',
  fontSize: '14px',
  fontWeight: '500',
  borderRadius: '10px',
  paddingBlock: '12px',
  paddingInline: '16px',
};

const cancelButtonStyle: SxProps<Theme> = {
  ...sharedButtonStyle,
  backgroundColor: '#FFFFFF',
};

function PlanDetailsSection() {
  const { user } = useUser();
  const confirm = useConfirm();
  const theme = useTheme();
  const navigate = useNavigate();

  const isIndependentShop = user?.business?.type === 'INDEPENDENT';
  const [isFreeModalOpen, setIsFreeModalOpen] = useState(false);

  const { data: subscriptionData } = useGetSubscription();
  const {
    data: stripeData,
    state: { isCanceled, isActive, isTrialing, isPaymentFailed, isFree },
  } = useGetSubscriptionFromStripe();
  const { product, status, paymentMethod, testClock, renewsAt, coupon } =
    stripeData || {};

  const onResumeError = () => {
    toast.error('Failed to resume subscription.');
  };
  const onCancelError = () => {
    toast.error('Failed to cancel subscription.');
  };
  const onResubscribeError = (error?: AxiosError) => {
    let msg = 'Failed to resubscribe.';
    if ((error?.response?.data as any)?.statusCode === 402) {
      msg = 'Failed to resubscribe. Payment method is invalid.';
    }
    toast.error(msg);
  };

  const { launchPortalMutation, isPendingCancel, isSuccessCancel } =
    useSubscriptionPortal({
      onError: onCancelError,
      onSuccess: (_, type) => {
        if (type === 'subscription_cancel') {
          posthog.capture('user_clicked_cancel_anyway', {
            business_id: user?.business?.id,
          });
        }
      },
    });
  const {
    resumeMutation,
    isPending: isPendingResume,
    isSuccess: isSuccessResume,
  } = useResumeSubscription({ onError: onResumeError });
  const {
    resubscribeMutation,
    isPending: isPendingResubscribe,
    isSuccess: isSuccessResubscribe,
  } = useResubscribeSubscription({
    onError: onResubscribeError,
  });

  const now = testClock ? new Date(testClock.frozen_time * 1000) : new Date();

  const isRenewInFuture = !!renewsAt && new Date(renewsAt) > now;
  const isPausedFreeTrial = status === 'paused';
  const isResumePrimaryColor = isPausedFreeTrial && !!paymentMethod;

  const canResume =
    (isCanceled && isRenewInFuture && status !== 'canceled') ||
    isPausedFreeTrial;
  const isCancelDisabled = isPendingCancel || isSuccessCancel;
  const isUpgradeDisabled = isPendingCancel || isSuccessCancel;
  const isResumeDisabled = isPendingResume || isSuccessResume;
  const isResubscribeDisabled = isPendingResubscribe || isSuccessResubscribe;
  const isUpgradeAvailable =
    subscriptionData?.subscription?.plan.type === 'ARTIST';

  const renewalDateString = renewsAt
    ? format(new Date(renewsAt), 'MMMM d, y')
    : '';
  const shortRenewalDateString = renewsAt
    ? format(new Date(renewsAt), 'MMMM d')
    : '';

  const productPriceDollarAmount = formatDollarAmount(
    (product?.price ?? 0) / 100,
  );
  const totalPriceDollarAmount = useMemo(() => {
    let priceCents = product?.price ?? 0;
    if (coupon?.valid) {
      if (coupon?.percent_off) {
        priceCents -= (coupon.percent_off / 100) * priceCents;
      }
      if (coupon?.amount_off) {
        priceCents -= coupon.amount_off;
      }
    }
    return formatDollarAmount(Math.max(0, priceCents) / 100);
  }, [coupon?.amount_off, coupon?.percent_off, coupon?.valid, product?.price]);

  const onUpgradePlan = () => {
    navigate(ROUTES.plans);
  };

  const onLaunchCancelPortal = () => {
    launchPortalMutation.mutate('subscription_cancel');
  };

  const onCancelSubscription = async () => {
    posthog.capture('user_clicked_cancel_plan', {
      business_id: user?.business?.id,
    });

    if (isIndependentShop) {
      setIsFreeModalOpen(true);
      return;
    }
    onLaunchCancelPortal();
  };

  const onResumeSubscription = async () => {
    if (!paymentMethod) {
      toast.error('Please add a payment method to resume.');
      return;
    }

    if (isPausedFreeTrial) {
      const accepted = await confirm({
        dismissible: true,
        title: 'Resume Subscription',
        message: `Are you sure you want to resume your subscription? The billing cycle will be reset and you will be charged the total amount (${productPriceDollarAmount}) immediately.`,
      });
      if (!accepted) return;
    }
    resumeMutation.mutate();
  };

  const onResubscribe = async () => {
    if (!paymentMethod) {
      toast.error('Please add a payment method to resubscribe.');
      return;
    }

    const accepted = await confirm({
      dismissible: true,
      title: 'Resubscribe',
      message: `Are you sure you want to resubscribe? This will create a new subscription and you will be charged the total amount (${productPriceDollarAmount}) immediately.`,
    });
    if (!accepted) return;

    resubscribeMutation.mutate();
  };

  if (!product) return null;

  return (
    <>
      <Section title={status === 'canceled' ? 'Previous Plan' : 'Current Plan'}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body1" color={theme.palette.medGrey.main}>
            {product.name}
          </Typography>
          {!isTrialing && (
            <Typography variant="body1" color={theme.palette.medGrey.main}>
              {totalPriceDollarAmount} per month
              {!isCanceled && isActive && ` - Renews on ${renewalDateString}`}
              {(!isActive || isCanceled) &&
                !isPaymentFailed &&
                isRenewInFuture &&
                status !== 'canceled' &&
                ` - Access will end on ${renewalDateString}`}
              {((!isActive && !isRenewInFuture) || status === 'canceled') &&
                ' - Access has ended'}
              {isPaymentFailed && ' - Payment is past due'}
            </Typography>
          )}
          {isTrialing && (
            <>
              <Typography variant="body1" color={theme.palette.medGrey.main}>
                Free trial ending on {renewalDateString}
              </Typography>
              <Typography variant="body1" color={theme.palette.medGrey.main}>
                {totalPriceDollarAmount} per month
                {!isCanceled && ` due starting ${shortRenewalDateString}`}
              </Typography>
            </>
          )}
          {!!coupon?.valid && (
            <Typography variant="body1" color={theme.palette.medGrey.main}>
              Coupon applied: {coupon.name}
            </Typography>
          )}
          {!isCanceled && isActive && !isFree && (
            <Stack direction="row" gap={1}>
              <LoadingButton
                onClick={onUpgradePlan}
                disabled={isUpgradeDisabled}
                aria-disabled={isUpgradeDisabled}
                variant="contained"
                sx={sharedButtonStyle}>
                {isUpgradeAvailable ? 'Upgrade Plan' : 'View Plans'}
              </LoadingButton>
              <LoadingButton
                loading={
                  !isIndependentShop && (isPendingCancel || isSuccessCancel)
                }
                onClick={onCancelSubscription}
                disabled={isCancelDisabled}
                aria-disabled={isCancelDisabled}
                variant="contained"
                color="inherit"
                sx={cancelButtonStyle}>
                Cancel Subscription
              </LoadingButton>
            </Stack>
          )}
          {canResume && (
            <LoadingButton
              loading={isPendingResume || isSuccessResume}
              onClick={onResumeSubscription}
              disabled={isResumeDisabled}
              aria-disabled={isResumeDisabled}
              variant="contained"
              color={isResumePrimaryColor ? 'primary' : 'inherit'}
              sx={{
                ...cancelButtonStyle,
                backgroundColor: isResumePrimaryColor
                  ? undefined
                  : (cancelButtonStyle as CSSProperties).backgroundColor,
              }}>
              Resume Subscription
            </LoadingButton>
          )}
          {status === 'canceled' && (
            <LoadingButton
              loading={isPendingResubscribe || isSuccessResubscribe}
              onClick={onResubscribe}
              disabled={isResubscribeDisabled}
              aria-disabled={isResubscribeDisabled}
              variant="contained"
              color="inherit"
              sx={cancelButtonStyle}>
              Resubscribe
            </LoadingButton>
          )}
        </Box>
      </Section>
      {isIndependentShop && (
        <FreeSubscriptionModal
          isOpen={isFreeModalOpen}
          onClose={() => setIsFreeModalOpen(false)}
          cancel={{
            action: onLaunchCancelPortal,
            isLoading: isPendingCancel || isSuccessCancel,
          }}
        />
      )}
    </>
  );
}

export default PlanDetailsSection;
